import React, { useState, useRef } from "react"
import PropTypes from "prop-types"
import { useFlexSearch } from "react-use-flexsearch"
import styled from "styled-components"
import { RemoveScrollBar } from "react-remove-scroll-bar"

import { BiArrowToRight as DefaultCollapseIcon } from "react-icons/bi"
import { GrClose as DefaultResetIcon } from "react-icons/gr"
import { AiOutlineSearch as DefaultSearchIcon } from "react-icons/ai"

const FlexSearchBox = props => {
  const {
    className,
    indexUrl,
    noResultsMessage,
    collapseIcon: inputCollapseIcon,
    resetIcon: inputResetIcon,
    searchIcon: inputSearchIcon,
    searchPlaceholder,
    storeUrl,
  } = props

  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const [store, setStore] = useState(null)
  const [index, setIndex] = useState(null)
  const searchInputRef = useRef()

  const [collapseIcon] = useState(() => {
    if (inputCollapseIcon) {
      return inputCollapseIcon
    }
    return <DefaultCollapseIcon></DefaultCollapseIcon>
  })

  const [resetIcon] = useState(() => {
    if (inputResetIcon) {
      return inputResetIcon
    }
    return <DefaultResetIcon></DefaultResetIcon>
  })

  const [searchIcon] = useState(() => {
    if (inputSearchIcon) {
      return inputSearchIcon
    }

    return <DefaultSearchIcon></DefaultSearchIcon>
  })

  const handleChange = async event => {
    setQuery(event.target.value)
    if (!store) {
      const fetchedStore = await fetch(storeUrl)
      const jsonStore = await fetchedStore.json()
      setStore(jsonStore)
    }
    if (!index) {
      const fetchedIndex = await fetch(indexUrl)
      const jsonIndex = await fetchedIndex.text()
      setIndex(jsonIndex)
    }
  }

  const handleSearchBlur = () => {
    setTimeout(() => {
      if (open) {
        setOpen(false)
      }
      setQuery("")
    }, 200)
  }

  const handleSubmit = event => {
    event.preventDefault()
  }

  const handleOpenSearch = () => {
    setOpen(true)
    if (searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }

  return (
    <div className={`${className}${open ? " open" : ""}`}>
      <span className="toggle-search-button">
        {open ? null : <span onClick={handleOpenSearch}>{searchIcon}</span>}
      </span>
      {
        <div
          className={`search-field-container${
            query.length > 2 ? " searching" : ""
          }`}
        >
          {open ? <RemoveScrollBar /> : null}
          <form className="search-form" onSubmit={handleSubmit}>
            <span className="toggle-search-button">
              {open ? (
                <span
                  onClick={() => {
                    setOpen(false)
                  }}
                >
                  {collapseIcon}
                </span>
              ) : null}
            </span>
            <input
              type="search"
              className={`search-field${open ? " open" : ""}`}
              placeholder={searchPlaceholder}
              name="s"
              value={query}
              onFocus={handleOpenSearch}
              onChange={handleChange}
              onBlur={handleSearchBlur}
              ref={searchInputRef}
            />
            {open && query && (
              <span className="search-reset" onClick={() => setQuery("")}>
                {resetIcon}
              </span>
            )}
          </form>
        </div>
      }

      {open && query.length >= 3 && store && index && (
        <SearchResults
          query={query}
          store={store}
          index={index}
          noResultsMessage={noResultsMessage}
        ></SearchResults>
      )}
    </div>
  )
}

FlexSearchBox.defaultProps = {
  noResultsMessage: "No results found",
  searchPlaceholder: "Search...",
  searchIcon: null,
}

FlexSearchBox.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  collapseIcon: PropTypes.element,
  indexUrl: PropTypes.string,
  noResultsMessage: PropTypes.string,
  resetIcon: PropTypes.element,
  searchIcon: PropTypes.element,
  searchPlaceholder: PropTypes.string,
  storeUrl: PropTypes.string,
}

const StyledFlexSearchBox = styled(FlexSearchBox)`
  display: block;
  position: relative;
  width: 100%;
  .toggle-search-button {
    position: absolute;
    display: flex;
    align-items: center;
    cursor: pointer;
    z-index: 30;
    top: 0px;
    bottom: 0px;
    margin: auto 0;
    right: 1rem;
    font-size: 1.5rem;
  }

  .search-field-container {
    height: 100%;
  }
  .search-form {
    position: relative;
    margin: 0 0 0 auto;
    height: 100%;
    width: 0;
    overflow: hidden;
    transition: width 500ms ease-out;
    border: none;
  }
  &.open {
    .search-form {
      border: 1px solid #efefef;
      width: 100%;
    }
    .toggle-search-button {
      left: 1rem;
      right: auto;
    }
  }
  .search-field {
    padding: 1rem 3rem; // allocating space for icons
    border: none;
    outline-color: transparent;
    width: 100%;
    height: 100%;
    background: transparent;
    &::-webkit-search-cancel-button {
      display: none;
    }
    &:focus {
      background: transparent;
    }
    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &:-webkit-autofill:active {
      transition: background-color 5000s ease-in-out 0s;
    }
    outline-color: #fff;
  }

  .search-reset {
    position: absolute;
    right: 1rem;
    top: 0;
    bottom: 0;
    margin-top: auto;
    margin-bottom: auto;
    display: flex;
    align-items: center;
    cursor: pointer;
  }
  .search-button-container {
    display: flex;
    overflow: hidden;
    justify-content: space-between;
    align-items: center;
  }
  .search-results {
    position: fixed;
    width: 100vw;
    height: 100vh;
    top: 4rem;
    left: 0;
    overflow: auto;
    background: #efefef;

    .search-result {
      &:nth-child(odd) {
        background: #fafafa;
      }
      &:nth-child(even) {
        background: #efefef;
      }
      a {
        display: block;
        width: 100%;
        height: 100%;
        padding: 2rem 1rem;
        .title {
          font-size: 1.2rem;
        }
        &:hover {
          background: lightblue;
        }
      }
    }

    .find-string {
      background: yellow;
      padding: 0.3rem;
    }
  }

  @media only screen and (min-width: ${props => props.lgScreen}) {
    .search-results-container {
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      text-align: center;
      background: #2f2f2f;
    }
    .search-field-container {
      height: 3rem;
      display: flex;
      justify-content: center;
      &.searching {
        position: fixed;
        width: 100vw;
        left: 0;
        right: 0;
        top: 1rem;
        z-index: 2;

        .search-field {
          background: #fff;

          &:focus {
            background: #fff;
          }
        }
        .search-form {
          width: 15rem;
          margin: auto;
        }
      }
    }
    .search-results {
      top: 6rem;
      width: 60vw;
      height: 80vh;
      position: fixed;
      margin: 0 auto;
      left: 0;
      right: 0;
      text-align: left;
    }
  }
`

StyledFlexSearchBox.defaultProps = {
  smScreen: "640px",
  mdScreen: "768px",
  lgScreen: "1024px",
  xlScreen: "1280px",
  "2xlScreen": "1536px",
}

const sliceContent = (content, findString, desiredTextLength = 100) => {
  const lowerFindString = findString.toLowerCase()
  const sanitizedContent = content.replace(/<[^>]*>?/gm, "")
  const lowerSanitizedContent = sanitizedContent.toLowerCase()
  const halfDesiredTextLength = parseInt(desiredTextLength / 2, 10)
  const stringPosition = lowerSanitizedContent.indexOf(lowerFindString)
  let initialPosition = 0
  let startText = ""
  let endText = ""
  if (stringPosition !== -1) {
    if (stringPosition - halfDesiredTextLength < 0) {
      initialPosition = 0
    } else {
      initialPosition = stringPosition - halfDesiredTextLength
    }
  }
  if (initialPosition !== 0) {
    startText = "..."
  }
  const finalPosition = initialPosition + desiredTextLength
  if (finalPosition <= sanitizedContent.length) {
    endText = "..."
  }
  const regex = new RegExp(findString, "ig")
  return (
    startText +
    sanitizedContent
      .slice(initialPosition, finalPosition)
      .replace(regex, match => {
        return `<span class="find-string">${match}</span>`
      }) +
    endText
  )
}

const SearchResults = props => {
  const { className, noResultsMessage, index, query, store } = props
  let results = useFlexSearch(query, index, store)

  results.sort((firstEl, secondEl) => {
    const firstTitleHasMatch = firstEl.title
      .toLowerCase()
      .includes(query.toLowerCase())
    const secondTitleHasMatch = secondEl.title
      .toLowerCase()
      .includes(query.toLowerCase())
    if (secondTitleHasMatch || firstTitleHasMatch) {
      return firstTitleHasMatch ? -1 : secondTitleHasMatch ? 1 : 0
    }
    const firstContentHasMatch = firstEl.content
      .toLowerCase()
      .includes(query.toLowerCase())
    const secondContentHasMatch = secondEl.content
      .toLowerCase()
      .includes(query.toLowerCase())
    if (secondContentHasMatch || firstContentHasMatch) {
      return firstTitleHasMatch ? -1 : secondTitleHasMatch ? 1 : 0
    }
    return 0
  })
  return (
    <div className={className}>
      <div className="search-results-container">
        <ul className="search-results">
          {results.length ? (
            results.map((element, i) => (
              <li className="search-result" key={`search-result-${i}`}>
                <a href={element.uri}>
                  <div
                    className="title"
                    dangerouslySetInnerHTML={{
                      __html: sliceContent(element.title, query),
                    }}
                  ></div>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: sliceContent(element.content, query),
                    }}
                  ></div>
                </a>
              </li>
            ))
          ) : (
            <li className="search-result">
              <a href="#">{noResultsMessage}</a>
            </li>
          )}
        </ul>
      </div>
    </div>
  )
}

SearchResults.propTypes = {
  className: PropTypes.string,
  index: PropTypes.string,
  noResultsMessage: PropTypes.string,
  query: PropTypes.string,
  store: PropTypes.object,
}

export { FlexSearchBox }
export default StyledFlexSearchBox
